import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radar, Loader2, Sparkles, Handshake, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const err = (e) => formatApiError(e?.response?.data?.detail) || "Scout failed.";
const ROLE_PRESETS = ["auto_repair", "dealership_fleet_seller", "parts_supplier", "buyer", "seller", "supplier"];

export function ScoutPanel({ onQueued }) {
  const [category, setCategory] = useState("Automotive");
  const [geography, setGeography] = useState("");
  const [targets, setTargets] = useState([{ role: "auto_repair", count: 2 }, { role: "dealership_fleet_seller", count: 2 }]);
  const [busy, setBusy] = useState(false);
  const [cohort, setCohort] = useState(null);
  const [attr, setAttr] = useState(null);

  const loadAttr = () => api.get("/admin/leads/scout/attribution").then((r) => setAttr(r.data)).catch(() => {});
  useEffect(() => { loadAttr(); }, []);

  const setTarget = (i, k, v) => setTargets((t) => t.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addRow = () => setTargets((t) => [...t, { role: "buyer", count: 1 }]);
  const rmRow = (i) => setTargets((t) => t.filter((_, idx) => idx !== i));

  const run = async () => {
    if (busy) return;
    if (!category.trim()) { toast.error("Enter a target category."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/scout", {
        category: category.trim(), geography: geography.trim(),
        targets: targets.map((t) => ({ role: t.role, count: Number(t.count) || 1 })), auto_recommend: true,
      });
      setCohort(data);
      toast.success(`Scout shortlisted ${data.candidate_count} candidates → Invitation Review queue.`);
      loadAttr(); onQueued?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="scout-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0A0F1C] mb-1"><Radar size={18} className="text-[#047857]" /> Participant Recruitment Scout</div>
        <p className="text-xs text-slate-500 mb-3 flex items-center gap-1"><ShieldCheck size={12} className="text-[#047857]" /> AI discovery + recommendation only. Candidates land in the Invitation Review queue — a senior human approves before any (simulated) send. Uses permitted public business info (SIMULATED source in preview).</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5"><Label>Target category</Label><Input data-testid="scout-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Automotive" /></div>
          <div className="grid gap-1.5"><Label>Geography (optional)</Label><Input data-testid="scout-geography" value={geography} onChange={(e) => setGeography(e.target.value)} placeholder="e.g. Texas, USA" /></div>
        </div>
        <div className="mt-3 space-y-2">
          <Label className="text-xs">Counterpart targets</Label>
          {targets.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`scout-target-${i}`}>
              <select data-testid={`scout-role-${i}`} value={t.role} onChange={(e) => setTarget(i, "role", e.target.value)} className="h-9 flex-1 rounded-md border border-slate-200 text-sm px-2">
                {ROLE_PRESETS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
              <Input data-testid={`scout-count-${i}`} type="number" min={1} max={10} value={t.count} onChange={(e) => setTarget(i, "count", e.target.value)} className="w-20" />
              {targets.length > 1 && <button data-testid={`scout-rm-${i}`} onClick={() => rmRow(i)} className="h-9 px-2 rounded border border-slate-200 text-slate-400 text-xs">✕</button>}
            </div>
          ))}
          <button data-testid="scout-add-target" onClick={addRow} className="text-xs text-[#047857]">+ add counterpart type</button>
        </div>
        <button data-testid="scout-run" disabled={busy} onClick={run} className="mt-3 h-9 px-4 rounded-md bg-[#047857] text-white text-sm flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Scouting…</> : <><Radar size={14} /> Find & shortlist candidates</>}
        </button>
      </div>

      {cohort && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="scout-cohort">
          <div className="text-sm font-semibold text-[#0A0F1C] flex items-center gap-2 mb-1"><Sparkles size={16} className="text-[#047857]" /> Sample cohort — {cohort.category}</div>
          <p className="text-xs text-slate-500 mb-3 flex items-start gap-1"><Handshake size={13} className="text-[#047857] mt-0.5" /> {cohort.pairing_rationale}</p>
          {cohort.pairings.map((p) => (
            <div key={p.role} className="mb-3">
              <div className="text-xs font-medium text-slate-600 mb-1">{p.role.replace(/_/g, " ")} · {p.candidates.length}</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {p.candidates.map((c) => (
                  <div key={c.lead_id} className="rounded-md border border-slate-100 p-2.5 text-xs" data-testid={`scout-cand-${c.lead_id}`}>
                    <div className="flex items-center justify-between"><span className="font-medium text-slate-700">{c.legal_name}</span><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">fit {c.fit_score}</span></div>
                    <div className="text-slate-400 text-[11px]">{c.industry} · {c.region} · rec {c.ai_recommendation}</div>
                    <p className="text-slate-500 mt-1 leading-snug">{c.ai_reasoning}</p>
                    <div className="mt-1 text-[10px] text-slate-400">{c.invitation_id ? "→ in Invitation Review queue (awaiting human approval)" : "shortlisted"}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {attr && Object.keys(attr.by_category || {}).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 overflow-x-auto" data-testid="scout-attribution">
          <div className="text-sm font-semibold text-[#0A0F1C] mb-2">Recruitment attribution by category</div>
          <table className="w-full text-xs">
            <thead className="text-slate-400"><tr><th className="text-left py-1 pr-3">Category</th>{attr.funnel_keys.map((k) => <th key={k} className="text-right px-1.5">{k.replace(/_/g, " ")}</th>)}</tr></thead>
            <tbody>
              {Object.entries(attr.by_category).map(([cat, f]) => (
                <tr key={cat} className="border-t border-slate-100"><td className="py-1 pr-3 font-medium text-slate-600">{cat}</td>{attr.funnel_keys.map((k) => <td key={k} className="text-right px-1.5 text-slate-600">{f[k]}</td>)}</tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-slate-400 mt-2">{attr.note}</p>
        </div>
      )}
    </div>
  );
}
